/** @jsx jsx */
import { Fields } from "@cd2/forms"
import { doo, formatDate, IComponentProp } from "@cd2/tools"
import { css, jsx } from "@emotion/core"
import React, { useState } from "react"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { IRecord } from "../../types/schema"
import { themeColors } from "../EOSThemeProvider"
import { CustomerTagFilter } from "../FormFields/CustomerTagFilter"
import { useGlobalState } from "../GlobalStateProvider"

export type ICustomerSelect2 = IRecord<"customers", "name"> & {
  branch: IRecord<"branches", "name">
  addresses: IRecord<"addresses", "postcode" | "importer_id">[]
  sales_rep?: IRecord<"sales_reps", "name"> | null
  lastOrderDate?: string
}

export const customerSelect2Attributes = [
  "name",
  "account_on_hold",
  "account_reference",
  { name: "sales_rep", attributes: ["name"] },
  { name: "branch", attributes: ["name"] },
  { name: "sub_customers", attributes: ["name"] },
  {
    name: "addresses",
    attributes: ["postcode", "importer_id"],
    filter: { not: { matches: { importer_id: "" } } },
    paginate: { page: 1, size: 2 },
    sort: {
      by: "isDefault",
      direction: "DESC",
    },
  },
  {
    as: "lastOrderDate",
    value: {
      first: {
        path: [
          {
            name: "orders",
            sort: { by: "created_at", direction: "desc" },
          },
          "created_at",
        ],
      },
    },
  },
]

interface ICustomerSelectList2Props {
  label?: React.ReactNode

  onChange(id?: string, customer?: ICustomerSelect2): void
  value?: string | ICustomerSelect2 | null

  disabled?: boolean
  isClearable?: boolean
  error?: string

  // Disables the branch filter
  allBranches?: boolean
  // Filter to only accessible branches
  showAccessibleBranches?: boolean
  // Specific branch to filter by, defaults to current user's branch
  branchId?: string

  includeBranchName?: boolean
  includeLastOrderDate?: boolean

  // Search filter replaces the default skeem filter on searching with query
  searchFilter?: ((query: string) => Record<string, any> | undefined) | Record<string, any>
  // Additional filter to apply on top of search and branch filters
  filter?: ((query: string) => Record<string, any> | undefined) | Record<string, any>

  tag?: IComponentProp<typeof CustomerTagFilter, "tag">
  tagValue?: IComponentProp<typeof CustomerTagFilter, "value">

  validations?: { presence?: boolean }
  menuPortalTarget?: HTMLElement | null
}

// Default behaviour is to filter to current branch
export function CustomerSelectList2(props: ICustomerSelectList2Props) {
  const {
    label,
    onChange,
    value,

    allBranches,
    showAccessibleBranches,

    branchId,

    // includeBranchName,
    // includeLastOrderDate,

    searchFilter: filterForSearch,
    filter: additionalFilter,

    tag,
    tagValue,

    disabled,
    isClearable,
    validations,
    error,
    menuPortalTarget,
  } = props
  const skeem = window.skeem
  const { currentUser } = useGlobalState()

  const [customer, setCustomer] = useState<ICustomerSelect2 | null>(
    value && typeof value !== "string" ? value : null,
  )

  function handleChange(id?: string, customer?: ICustomerSelect2) {
    onChange(id, customer)
  }

  useAsyncEffect(async () => {
    if (typeof value === "string") {
      const customerRecord = await skeem.fetch2<ICustomerSelect2>({
        first: {
          name: "customers",
          matches: { id: value },
          attributes: customerSelect2Attributes,
        },
      })
      setCustomer(customerRecord)
    } else {
      setCustomer(null)
    }
  }, [value])

  async function loadOptions(query: string) {
    if (query.length < 3) {
      return []
    }
    const branchFilter = { and: [] } as any

    if (allBranches) {
      // No top level branch filter
    } else if (showAccessibleBranches) {
      const branchIds = currentUser.accessibleBranches.map((b: any) => b.id)
      branchFilter.and.push({ matches: { branch: { or: branchIds } } })
    } else {
      branchFilter.and.push({ matches: { branch: branchId ?? currentUser.branch.id } })
    }

    const customFilter = { and: [] } as any
    if (tagValue) {
      customFilter.and.push({
        matches: {
          tag_values2: { some: { id: tagValue.id } },
        },
      })
    } else if (tag) {
      customFilter.and.push({
        matches: {
          tag_values2: { some: { tag: tag.id } },
        },
      })
    }

    // Filter used when searching with a query
    const searchFilter = doo(() => {
      if (!filterForSearch) {
        return {
          or: [
            {
              matches: { name: { like: query } },
            },
            {
              matches: { account_reference: { like: query } },
            },
          ],
        }
      }
      if (typeof filterForSearch === "function") {
        return filterForSearch(query)
      }
      return filterForSearch
    })

    // Additional filter applied on top of search and branch filters
    const filter = doo(() => {
      if (!additionalFilter) {
        return null
      }
      if (typeof additionalFilter === "function") {
        return additionalFilter(query)
      }
      return additionalFilter
    })

    const records = await window.skeem.fetch2<ICustomerSelect2[]>({
      model: {
        name: "customers",
        attributes: customerSelect2Attributes,
        filter: {
          and: [
            {
              or: [
                searchFilter,
                {
                  gt: [
                    {
                      count: {
                        path: [
                          {
                            name: "addresses",
                            filter: {
                              matches: { importer_id: { not: "" }, postcode: { like: query } },
                            },
                          },
                        ],
                      },
                    },
                    { value: 0 },
                  ],
                },
              ],
            },
            filter,
            ...branchFilter.and,
            ...customFilter.and,
          ].filter(Boolean),
        },
        sort: { by: "name", direction: "asc" },
        paginate: { size: 50 },
      },
    })
    return records
  }

  return (
    <Fields.AsyncSelect<ICustomerSelect2>
      label={label}
      showRequiredStar={!!validations?.presence}
      onChange={opt => handleChange(opt?.id, opt)}
      value={typeof value == "object" ? value : customer}
      loadOptions={(query: string) => loadOptions(query)}
      getOptionValue={opt => opt.id}
      getOptionLabel={opt => opt.name}
      disabled={disabled}
      isClearable={isClearable}
      error={error}
      menuPortalTarget={menuPortalTarget}
      noOptionsMessage={({ inputValue }) =>
        inputValue.length < 3 ? "Type 3 characters to search..." : "No results"
      }
      components={customerSelectComponents({
        ...props,
      })}
    />
  )
}

CustomerSelectList2.attributes = customerSelect2Attributes

function customerSelectComponents({
  includeBranchName,
  includeLastOrderDate,
}: {
  includeBranchName?: boolean
  includeLastOrderDate?: boolean
}) {
  function SubText(props: any) {
    const branchText = includeBranchName ? props.data.branch?.name : ""

    const subTextLine = [
      <span key={`ac-ref-${props.data.account_reference}`} style={{}}>
        {props.data.account_reference}
      </span>,
      props.data.addresses?.[0]?.postcode && (
        <span key={`postcode-${props.data.addresses[0].postcode}`} style={{}}>
          {props.data.addresses[0].postcode}
        </span>
      ),
      branchText && <span style={{}}>{branchText}</span>,
    ]
      .filter(Boolean)
      .map((node, index) => {
        if (index > 0) {
          node.props.style["marginLeft"] = "8px"
        }
        return node
      })

    return (
      <div
        css={css`
          color: #999;
          font-size: 10px;
        `}
      >
        {subTextLine}
      </div>
    )
  }

  return {
    SingleValue: (props: any) => {
      const { innerProps, getStyles } = props

      return (
        <div
          {...innerProps}
          style={{
            ...getStyles("singleValue", props),
          }}
        >
          <div>{props.data.name} </div>
          <SubText data={props.data} />
          {includeLastOrderDate && props.data.lastOrderDate && (
            <div
              css={css`
                color: #999;
                font-size: 10px;
                font-weight: 500;
              `}
            >
              last order: {formatDate(props.data?.lastOrderDate)}
            </div>
          )}
        </div>
      )
    },
    Option: (props: any) => {
      const { innerProps, innerRef, getStyles } = props

      return (
        <div
          ref={innerRef}
          {...innerProps}
          style={{
            ...getStyles("option", props),
            backgroundColor: props.data.account_on_hold
              ? themeColors.lightRed
              : getStyles("option", props).backgroundColor,
          }}
        >
          <div>
            {props.data.name}{" "}
            {props.data.account_on_hold ? (
              <span>
                {" "}
                <b>(ON HOLD)</b>
              </span>
            ) : undefined}
          </div>

          <SubText data={props.data} />
          {includeLastOrderDate && props.data.lastOrderDate && (
            <div
              css={css`
                color: #999;
                font-size: 10px;
                font-weight: 500;
              `}
            >
              last order: {formatDate(props.data?.lastOrderDate)}
            </div>
          )}
        </div>
      )
    },
  }
}
